import {useEffect, useState} from 'react'
import {Heading} from '@/components/tailwind/heading'
import {FormerMember} from '@/models/former-member'
import {getFormerMembers} from '@/services/former-member-services'
import {useAuth} from '@/context/use-auth'
import {useReportsTranslation, useCommonTranslation} from '@/i18n/hooks'
import {Text} from '@/components/tailwind/text'
import {Input, InputGroup} from '@/components/tailwind/input'
import {MagnifyingGlassIcon} from '@heroicons/react/16/solid'
import {FormerMemberDataTable} from './former-member-data-table'
import {formerMemberColumns} from './former-member-columns'
import log from '@/lib/logger'

function FormerMemberListPage() {
    const [formerMembers, setFormerMembers] = useState<FormerMember[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const {token} = useAuth()
    const {t} = useReportsTranslation()
    const {t: tCommon} = useCommonTranslation()

    useEffect(() => {
        async function fetchFormerMembers() {
            if (!token) {
                log.error('No token available for fetching former members')
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                setError(null)

                const data = await getFormerMembers(token)
                setFormerMembers(data)
            } catch (err) {
                log.error('Error fetching former members:', err)
                setError(t('formerMembers.error'))
            } finally {
                setLoading(false)
            }
        }

        fetchFormerMembers()
    }, [token])

    if (loading) {
        return (
            <div className="container mx-auto py-6">
                <Text>{tCommon('loading')}</Text>
            </div>
        )
    }

    if (error) {
        return (
            <div className="container mx-auto py-6">
                <Text className="text-red-500">{error}</Text>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-6 space-y-6">
            <div>
                <Heading>{t('formerMembers.title')}</Heading>
                <Text>{t('formerMembers.description')}</Text>
            </div>

            <div>
                <InputGroup>
                    <MagnifyingGlassIcon/>
                    <Input
                        type="text"
                        placeholder={t('formerMembers.search.placeholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label={t('formerMembers.search.ariaLabel')}
                    />
                </InputGroup>
            </div>

            {formerMembers.length === 0 ? (
                <Text className="italic text-gray-500">{t('formerMembers.noMembers')}</Text>
            ) : (
                <FormerMemberDataTable
                    columns={formerMemberColumns}
                    data={formerMembers}
                    searchTerm={searchTerm}
                />
            )}
        </div>
    )
}

export default FormerMemberListPage
